import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiServices';
import notificationsService from '../../services/notificationsService';
import buildPayslipPdfAttachment from '../../utils/payslipPdf';
import config from '../../config/config';
import {
  Box, Card, Chip, IconButton, Paper, Skeleton, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, LinearProgress, Avatar, Divider,
  TextField, Select, MenuItem, InputAdornment, FormControl, InputLabel,
  Menu, ListItemIcon,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon            from '@mui/icons-material/Add';
import EditIcon           from '@mui/icons-material/Edit';
import DeleteIcon         from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon    from '@mui/icons-material/ReceiptLong';
import LockIcon           from '@mui/icons-material/Lock';
import LockOpenIcon       from '@mui/icons-material/LockOpen';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';
import EmailIcon          from '@mui/icons-material/Email';
import SendIcon           from '@mui/icons-material/Send';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import CancelIcon         from '@mui/icons-material/Cancel';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import DownloadIcon       from '@mui/icons-material/Download';
import SearchIcon         from '@mui/icons-material/Search';
import PrintIcon          from '@mui/icons-material/Print';
import MoreVertIcon       from '@mui/icons-material/MoreVert';
import GridOnIcon         from '@mui/icons-material/GridOn';
import PictureAsPdfIcon   from '@mui/icons-material/PictureAsPdf';
import { Link } from 'react-router-dom';
import UploadFileIcon    from '@mui/icons-material/UploadFile';
import { buildRegisterKS, payslipNet } from '../../utils/payrollRegisterKS';
import { resolveFiscal } from '../../utils/fiscalPolicy';
import ImportSalariesDialog from './ImportSalariesDialog';
import { exportRegisterExcel, downloadRegisterPdf, printRegister } from '../../utils/payrollRegisterExport';

const MONTHS = [
  '', 'Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor',
  'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor',
];
const MONTH_COLORS = [
  '', '#3E8FA3','#0891B2','#3E8FA3','#16A34A','#65A30D','#CA8A04',
  '#D97706','#EA580C','#DC2626','#9333EA','#2563EB','#667085',
];

const fmt = (val) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR' }).format(val || 0);
const initials = (name = '') =>
  name.split(' ').map(n => n[0] || '').join('').toUpperCase().slice(0, 2) || '?';

// ─── buildEmailHTML ────────────────────────────────────────────────────────────
// Exported so the employee self-service payslip page can render the EXACT same
// document that is emailed to the worker.
export function buildEmailHTML(worker, listName, company, opts = {}) {
  // showZero=true → list every component even when it is 0.00 (used by the
  // employee self-service payslip page); the email keeps hiding empty lines.
  const showZero = !!opts.showZero;
  const gross   = parseFloat(worker.Salary || worker.salary || 0);
  const adv     = parseFloat(worker.Advances || worker.advances || 0);
  const extra   = parseFloat(worker.HourSalary || 0);
  const bonus   = parseFloat(worker.Bonus || worker.bonus || 0);
  const pen     = parseFloat(worker.Penalties || worker.penalties || 0);
  const pension = parseFloat(worker.employeeTaxFee || worker.PensionEmployee || 0);
  const pensionEr = parseFloat(worker.employerTaxFee || worker.PensionEmployer || 0);
  const tax     = parseFloat(worker.taxes || worker.Tax || 0);
  const healthIns = parseFloat(worker.HealthInsEmployee || 0);
  const healthInsEr = parseFloat(worker.HealthInsEmployer || 0);
  const net = payslipNet(worker); // një burim i vetëm për neton (hoisted më poshtë)

  // Hourly rate derived from the (gross) salary — 173.33 standard monthly hours.
  const MONTHLY_HOURS = 173.33;
  const hourlyRate = gross ? gross / MONTHLY_HOURS : 0;

  const totalEarn = gross + extra + bonus;
  const totalDed  = pension + tax + healthIns + adv + pen;

  const co       = (company && typeof company === 'object') ? company : { name: company };
  const coName   = co.name || co.CompanyName || 'Kompania';
  const coNui    = co.nui || co.NUI || co.FiscalNumber || '';
  const coAddr   = co.address || co.Address || '';
  const coCity   = co.city || co.City || '';
  const fullName = worker.FullName || `${worker.FirstName || ''} ${worker.LastName || ''}`.trim() || '';
  const position = worker.Position || worker.position || '';
  const nid      = worker.NID || worker.PersonalNumber || worker.personal_number || '';
  const bank     = worker.BankAccount || worker.bank_account || '';
  // General-data fields (Treasury-style). Missing ones render as "—".
  const workerNo    = worker.WorkerNo || worker.EmployeeNo || '';
  const contractNo  = worker.ContractNo || '';
  const contractTyp = worker.ContractType || '';
  const organization = worker.Organization || coName;
  const institution  = worker.Institution || coCity || '';
  const bankName     = worker.BankName || co.bank || '';
  const workHours    = worker.WorkHours || co.workHours || '';
  // Experience from hire date → "X vite, Y muaj".
  let experience = '';
  const hd = worker.HireDate || worker.hire_date || '';
  if (hd) {
    const d = new Date(hd);
    if (!isNaN(d)) {
      let months = (new Date().getFullYear() - d.getFullYear()) * 12 + (new Date().getMonth() - d.getMonth());
      if (months < 0) months = 0;
      experience = `${Math.floor(months / 12)} vite, ${months % 12} muaj`;
    }
  }
  const dash = (v) => (v || v === 0 ? v : '—');

  const earnings = [
    { label: 'Paga bazë bruto',          v: gross,      sign: '',  color: '#0F172A', always: true },
    { label: 'Çmimi i orës (€/orë)',     v: hourlyRate, sign: '',  color: '#64748B', always: true, info: true },
    { label: 'Orë shtesë',               v: extra,      sign: '+', color: '#059669' },
    { label: 'Bonus',                    v: bonus,      sign: '+', color: '#059669' },
  ];
  const deductions = [
    { label: 'Tatimi mbi të ardhura',        v: tax,       sign: '−', color: '#DC2626' },
    { label: 'Sig. pensionale (punëtori)',   v: pension,   sign: '−', color: '#DC2626' },
    { label: 'Sig. shëndetësor (punëtori)',  v: healthIns, sign: '−', color: '#DC2626' },
    { label: 'Avanci',                       v: adv,       sign: '−', color: '#DC2626' },
    { label: 'Penalitete',                   v: pen,       sign: '−', color: '#DC2626' },
  ];
  const employer = [
    { label: 'Pension (punëdhënësi)',         v: pensionEr,   sign: '', color: '#475569' },
    { label: 'Sig. shëndetësor (punëdhënësi)', v: healthInsEr, sign: '', color: '#475569' },
  ];

  const fmtKs = (v) => Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';
  const MN = ['', 'janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor'];
  const mNum = parseInt(worker.Month, 10) || 0;
  const yNum = parseInt(worker.Year, 10) || 0;
  const periodLabel = (mNum >= 1 && mNum <= 12 && yNum)
    ? `1 ${MN[mNum]} ${yNum} - ${new Date(yNum, mNum, 0).getDate()} ${MN[mNum]} ${yNum}`
    : listName;
  let genDate = '';
  try { genDate = new Date().toLocaleDateString('sq-AL') + ' ' + new Date().toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' }); } catch (_) { genDate = ''; }
  const logo = co.logo || co.Logo || '';

  const keep = (r) => r.always || showZero || r.v > 0;
  const payRow = (r) => {
    const muted = !(r.v > 0 || r.always);
    return `<tr>
      <td style="padding:5px 14px;font-size:12.5px;color:${muted ? '#94A3B8' : '#1F2937'};"> - ${r.label}</td>
      <td align="right" style="padding:5px 14px;font-size:12.5px;font-weight:700;white-space:nowrap;color:${muted ? '#94A3B8' : '#111827'};">${fmtKs(r.v)}</td>
    </tr>`;
  };
  const payBox = (title, items) => `
    <td width="50%" valign="top" style="padding:0 6px;">
      <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #9DB4CC;border-radius:4px;overflow:hidden;background:#E9F1FA;">
        <tr><td colspan="2" style="padding:7px 14px;font-size:12px;font-weight:800;color:#1F3A5F;border-bottom:1px solid #C3D4E6;">${title}</td></tr>
        ${items.filter(keep).map(payRow).join('')}
      </table>
    </td>`;
  const field = (label, value) => `<tr>
      <td style="padding:3px 0;font-size:12px;color:#475569;white-space:nowrap;vertical-align:top;">${label}:</td>
      <td style="padding:3px 0 3px 10px;font-size:12px;font-weight:700;color:#0F172A;">${value || '—'}</td>
    </tr>`;
  const moneyBar = (label, value) => `
    <tr><td style="padding:6px 0;">
      <table width="100%" cellpadding="0" cellspacing="0" style="background:#FBE4D4;border:1px solid #EBC4A6;border-radius:4px;">
        <tr><td align="center" style="padding:11px;font-size:15px;font-weight:800;color:#7C2D12;">${label}: ${fmtKs(value)}</td></tr>
      </table>
    </td></tr>`;

  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"/></head>
<body style="margin:0;padding:0;background:#F1F2F4;font-family:Arial,Helvetica,sans-serif;color:#111827;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#F1F2F4;padding:24px 0;">
    <tr><td align="center">
      <table width="720" cellpadding="0" cellspacing="0" style="background:#fff;border:1px solid #E2E8F0;">
        <tr><td style="padding:22px 32px 10px;text-align:center;">
          ${logo ? `<img src="${logo}" alt="" style="height:54px;margin-bottom:8px;"/>` : ''}
          <p style="margin:0;font-size:15px;font-weight:800;color:#0F172A;">${coName}</p>
          ${(coNui || coAddr || coCity) ? `<p style="margin:4px 0 0;font-size:11.5px;color:#475569;">${coNui ? `NUI: ${coNui}` : ''}${(coNui && (coAddr || coCity)) ? ' · ' : ''}${[coAddr, coCity].filter(Boolean).join(', ')}</p>` : ''}
          <p style="margin:8px 0 0;font-size:11px;color:#64748B;font-style:italic;">Fletëpagesë / Pay slip</p>
        </td></tr>
        <tr><td style="padding:0 32px;"><hr style="border:none;border-top:1px solid #CBD5E1;margin:0;"/></td></tr>

        <tr><td style="padding:14px 32px 6px;">
          <table width="100%" cellpadding="0" cellspacing="0" style="background:#DCE9F8;border:1px solid #AFC6E0;border-radius:4px;">
            <tr><td align="center" style="padding:9px;font-size:13px;font-weight:800;color:#1F3A5F;">Pagesa për periudhën ${periodLabel}</td></tr>
          </table>
        </td></tr>

        <tr><td style="padding:8px 32px 0;"><p style="margin:0 0 6px;font-size:12.5px;font-weight:800;color:#0F172A;">Të dhëna gjenerale</p></td></tr>
        <tr><td style="padding:0 32px;">
          <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #9DB4CC;border-radius:4px;"><tr valign="top">
            <td width="50%" style="padding:10px 16px;border-right:1px solid #D6E0EC;">
              <table width="100%">${field('Nëpunësi', fullName)}${field('Numri personal', dash(nid))}${field('Numri i punëtorit', dash(workerNo))}${field('Numri i kontratës', dash(contractNo))}</table>
            </td>
            <td width="50%" style="padding:10px 16px;">
              <table width="100%">${field('Orët e punës', dash(workHours))}${field('Lloji i kontratës', dash(contractTyp))}${field('Banka', dash(bankName))}${field('Numri i llogarisë', dash(bank))}</table>
            </td>
          </tr></table>
        </td></tr>

        <!-- Organization -->
        <tr><td style="padding:8px 32px 0;">
          <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #9DB4CC;border-radius:4px;">
            <tr><td style="padding:9px 16px;"><table width="100%">
              ${field('Organizata', dash(organization))}
              ${field('Institucioni', dash(institution))}
              ${field('Emërtimi i pozitës', dash(position))}
            </table></td></tr>
            <tr><td style="padding:8px 16px;border-top:1px solid #D6E0EC;">
              <table width="100%"><tr>
                <td style="font-size:12px;color:#475569;">Klasa: <strong style="color:#0F172A;">${dash(worker.Klasa)}</strong></td>
                <td align="center" style="font-size:12px;color:#475569;">Koeficienti: <strong style="color:#0F172A;">${dash(worker.Koeficienti)}</strong></td>
                <td align="right" style="font-size:12px;color:#475569;">Përvoja: <strong style="color:#0F172A;">${dash(experience)}</strong></td>
              </tr></table>
            </td></tr>
          </table>
        </td></tr>

        <tr><td style="padding:14px 32px 0;"><p style="margin:0 0 6px;font-size:12.5px;font-weight:800;color:#0F172A;">Detajet e pagesës</p></td></tr>
        <tr><td style="padding:0 26px;">
          <table width="100%" cellpadding="0" cellspacing="0"><tr valign="top">
            ${payBox('Paga / shtesat / kompenzimet', earnings)}
            ${payBox('Ndalesat', deductions)}
          </tr></table>
        </td></tr>
        ${(employer.some((r) => r.v > 0) || showZero) ? `
        <tr><td style="padding:8px 32px 0;">
          <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #D9E2EC;border-radius:4px;background:#F6F9FC;">
            <tr><td colspan="2" style="padding:6px 14px;font-size:11px;font-weight:800;color:#64748B;text-transform:uppercase;letter-spacing:.4px;">Kontributet e punëdhënësit (informative)</td></tr>
            ${employer.filter(keep).map(payRow).join('')}
          </table>
        </td></tr>` : ''}

        <tr><td style="padding:6px 32px 0;">
          <table width="100%" cellpadding="0" cellspacing="0">
            ${moneyBar('Paga bruto', totalEarn)}
            ${moneyBar('Paga neto', net)}
          </table>
        </td></tr>

        <tr><td style="padding:18px 32px 22px;">
          <table width="100%"><tr>
            <td style="font-size:10.5px;color:#94A3B8;">Faqe 1 prej 1</td>
            <td align="right" style="font-size:10.5px;color:#94A3B8;">${genDate}</td>
          </tr></table>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// ─── buildEmailNotificationHTML ──────────────────────────────────────────────
// The EMAIL body — a short, friendly notification. The full payslip travels as
// the attached PDF (buildPayslipPdfAttachment), so the body stays clean.
export function buildEmailNotificationHTML(worker, listName, company) {
  const co     = (company && typeof company === 'object') ? company : { name: company };
  const coName = co.name || co.CompanyName || 'Kompania';
  const name   = worker.FullName || worker.FirstName || '';
  const net    = payslipNet(worker);
  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"/></head>
<body style="margin:0;padding:0;background:#EEF2F5;font-family:'Segoe UI',Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#EEF2F5;padding:32px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 4px 18px rgba(15,23,42,.10);">
        <tr><td style="background:linear-gradient(135deg,#3E8FA3 0%,#2C6E7E 100%);padding:24px 30px;">
          <p style="margin:0;color:#fff;font-size:18px;font-weight:800;letter-spacing:.3px;">${coName}</p>
          <p style="margin:4px 0 0;color:#D6EAF0;font-size:12px;">Njoftim për fletëpagesë</p>
        </td></tr>
        <tr><td style="padding:28px 30px 6px;">
          <p style="margin:0;color:#0F172A;font-size:15px;">Përshëndetje <strong>${name}</strong>,</p>
          <p style="margin:12px 0 0;color:#475569;font-size:13.5px;line-height:1.6;">
            Ju njoftojmë se <strong>fletëpagesa juaj për periudhën ${listName}</strong> është procesuar dhe është gati.
            Detajet e plota — të ardhurat, ndalesat dhe kontributet — i gjeni në dokumentin <strong>PDF të bashkëngjitur</strong>.
          </p>
        </td></tr>
        <tr><td style="padding:18px 30px 4px;">
          <table width="100%" cellpadding="0" cellspacing="0" style="background:#F0F7F9;border:1px solid #CFE3E9;border-radius:10px;">
            <tr>
              <td style="padding:16px 20px;color:#2C6E7E;font-size:13px;font-weight:700;">Paga neto për pagesë</td>
              <td align="right" style="padding:16px 20px;color:#2C6E7E;font-size:22px;font-weight:800;">${fmt(net)}</td>
            </tr>
          </table>
        </td></tr>
        <tr><td style="padding:14px 30px 0;">
          <table cellpadding="0" cellspacing="0" style="background:#F8FAFC;border:1px dashed #CBD5E1;border-radius:8px;">
            <tr><td style="padding:11px 16px;color:#475569;font-size:12.5px;">📎 Bashkëngjitur: <strong style="color:#0F172A;">Fletëpagesa — ${listName}.pdf</strong></td></tr>
          </table>
        </td></tr>
        <tr><td style="padding:18px 30px 24px;">
          <p style="margin:0;color:#94A3B8;font-size:11.5px;line-height:1.6;">
            Ky është një njoftim automatik nga sistemi i Burimeve Njerëzore — ju lutemi mos iu përgjigjni këtij emaili.
            Për çdo pyetje rreth pagës, kontaktoni departamentin e Burimeve Njerëzore.
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// ─── buildEmailText (plain-text fallback) ────────────────────────────────────
function buildEmailText(worker, listName, company) {
  const co = (company && typeof company === 'object') ? company : { name: company };
  const coName = co.name || co.CompanyName || 'Kompania';
  return [
    `${coName} — Njoftim për fletëpagesë`,
    '',
    `Përshëndetje ${worker.FullName || worker.FirstName || ''},`,
    '',
    `Fletëpagesa juaj për periudhën ${listName} është gati.`,
    `Paga neto për pagesë: ${fmt(payslipNet(worker))}`,
    '',
    'Detajet e plota i gjeni në dokumentin PDF të bashkëngjitur.',
    'Ky është një njoftim automatik — për pyetje kontaktoni departamentin e Burimeve Njerëzore.',
  ].join('\n');
}

// ─── StatusIcon ───────────────────────────────────────────────────────────────
function StatusIcon({ status }) {
  if (status === 'sent')    return <CheckCircleIcon sx={{ fontSize: 16, color: '#3E8FA3' }} />;
  if (status === 'failed')  return <CancelIcon      sx={{ fontSize: 16, color: '#DC2626' }} />;
  if (status === 'no-email')return <WarningAmberIcon sx={{ fontSize: 16, color: '#D97706' }} />;
  if (status === 'sending') return <LinearProgress  sx={{ width: 40, height: 4, borderRadius: 2 }} />;
  return <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'grey.300' }} />;
}

// ─── StatusChip ───────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const { t } = useTranslation();
  const MAP = {
    sent:       { label: t('salariesList.sent'),         color: 'success' },
    failed:     { label: t('salariesList.failedChip'),   color: 'error'   },
    'no-email': { label: t('salariesList.noEmailChip'),  color: 'warning' },
    sending:    { label: t('salariesList.sending'),      color: 'info'    },
    pending:    { label: t('salariesList.pending'),      color: 'default' },
  };
  const m = MAP[status] || MAP.pending;
  return <Chip size="small" label={m.label} color={m.color} variant="outlined" sx={{ fontSize: 10 }} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function SalariesList() {
  const { t } = useTranslation();
  const [lists,        setLists]        = useState([]);
  const [totals,       setTotals]       = useState({});
  const [loading,      setLoading]      = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [toast,        setToast]        = useState(null);

  // Email dialog state
  const [emailDialog,    setEmailDialog]    = useState(null); // { list }
  const [emailWorkers,   setEmailWorkers]   = useState([]);   // enriched salary rows
  const [emailStatuses,  setEmailStatuses]  = useState({});   // { workerId: 'pending'|'sending'|'sent'|'failed'|'no-email' }
  const [emailSending,   setEmailSending]   = useState(false);
  const [emailDone,      setEmailDone]      = useState(false);
  const [emailLoading,   setEmailLoading]   = useState(false);
  // Objekti i plotë i cilësimeve, jo vetëm emri: fletëpagesa dhe emaili e
  // marrin logon prej këtu. Më parë kalohej një varg, ndaj logoja nuk
  // arrinte kurrë — as te PDF-ja, as te trupi HTML që e mbështet prej kohësh.
  const [company, setCompany] = useState({});

  // ── filters ───────────────────────────────────────────────────────────────
  const [filterYear,   setFilterYear]   = useState('');
  const [filterMonth,  setFilterMonth]  = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  useEffect(() => {
    apiService().get('settings').then(r => {
      if (r.success && r.data?.length) setCompany(r.data[0] || {});
    });
  }, []);

  // ── load lists ──────────────────────────────────────────────────────────────
  // Një kërkesë e vetme për totalet e të gjitha listave.
  //
  // Më parë thirrej `getSalariesByListId` një herë PËR ÇDO listë, dhe rreshtat
  // mblidheshin këtu: me dymbëdhjetë muaj bëheshin trembëdhjetë kërkesa, secila
  // duke tërhequr çdo rresht page me bashkimin e punëtorit — vetëm që të
  // nxirreshin gjashtë shuma. Tani i mbledh serveri, me të njëjtin rregull për
  // neton si `payslipNet` këtu, që totali i listës të mos ndryshojë nga ai i
  // fletëpagesës.
  const loadTotals = useCallback(() => {
    apiService()
      .query('getSalaryListTotals')
      .then((resp) => {
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        setTotals(Object.fromEntries(rows.map((r) => [r.listId, r])));
      })
      .catch(() => {});
  }, []);

  const [menuFor, setMenuFor] = useState(null);   // { row, el }

  // Importi nga Excel — dialog i vetëm, i pavarur nga kjo faqe përveç
  // rifreskimit pas ruajtjes: lista e re duhet të dalë menjëherë te tabela.
  const [importOpen, setImportOpen] = useState(false);

  // ── Regjistri zyrtar "Lista e Pagave" ─────────────────────────────────────
  // Ndërtohet me kërkesë e jo në ngarkim: kërkon rreshtat e plotë të pagës plus
  // brezat tatimorë, dhe shumica e vizitave te kjo faqe nuk e prekin fare.
  const [regBusy, setRegBusy] = useState(null);   // `${listId}:${veprimi}`
  const buildRegister = useCallback(async (row) => {
    const [sRes, fpRes, fdRes, setRes] = await Promise.all([
      apiService().query('getSalariesByListId', `id=${row.id}`),
      apiService().get('fiscalPoliciesExt'),
      apiService().get('fiscalPoliticsDetails'),
      apiService().get('settings'),
    ]);
    const rows = Array.isArray(sRes?.data) ? sRes.data : [];
    if (!rows.length) throw new Error('Kjo listë nuk ka rreshta page.');

    // Brezat e PERIUDHËS së listës, jo të politikës «aktive»: një regjistër i
    // vitit të kaluar nuk rillogaritet me shkallët e sotme. Pa politikë të
    // konfiguruar mbeten brezat e ligjit — kurse më parë kërkesa dështonte me
    // «nuk ka politikë aktive», dhe regjistri nuk dilte fare.
    const { ranges } = resolveFiscal({
      policies: Array.isArray(fpRes?.data) ? fpRes.data : [],
      details:  Array.isArray(fdRes?.data) ? fdRes.data : [],
      year:  row.year  ?? row.Year,
      month: row.month ?? row.Month,
    });

    return buildRegisterKS(rows, { list: row, company: setRes?.data?.[0] || {}, ranges });
  }, []);

  const runRegister = async (row, action) => {
    setRegBusy(`${row.id}:${action}`);
    try {
      const reg = await buildRegister(row);
      if (action === 'excel')      exportRegisterExcel(reg);
      else if (action === 'pdf')   await downloadRegisterPdf(reg);
      else if (action === 'print' && !(await printRegister(reg))) {
        setToast({ type: 'warning', msg: 'Shfletuesi e bllokoi dritaren e printimit — lejo dritaret kërcyese.' });
      }
    } catch (e) {
      setToast({ type: 'error', msg: e.message || 'Regjistri nuk u ndërtua.' });
    } finally {
      setRegBusy(null);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    apiService().get('salariesmonthlylists').then((resp) => {
      const records = Array.isArray(resp?.data) ? resp.data : [];
      setLists(records.sort((a, b) => b.year !== a.year ? b.year - a.year : b.month - a.month));
      loadTotals();
    }).catch(() => {}).finally(() => setLoading(false));
  }, [loadTotals]);

  useEffect(() => { load(); }, [load]);

  // ── delete ──────────────────────────────────────────────────────────────────
  const handleDelete = () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    apiService().delete('salariesmonthlylists', deleteTarget.id)
      .then(() => { setLists((p) => p.filter((x) => x.id !== deleteTarget.id)); setToast({ type: 'success', msg: t('salariesList.listDeleted') }); })
      .catch(() => setToast({ type: 'error', msg: t('common.errorOccurred') }))
      .finally(() => { setDeleting(false); setDeleteTarget(null); });
  };

  // ── open email dialog ────────────────────────────────────────────────────────
  const openEmailDialog = (list) => {
    setEmailDialog(list);
    setEmailDone(false);
    setEmailSending(false);
    setEmailStatuses({});
    setEmailWorkers([]);
    setEmailLoading(true);
    apiService().query('getSalariesByListId', `id=${list.id}`).then(resp => {
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      setEmailWorkers(rows);
      const init = {};
      rows.forEach(r => { init[r.WorkerId || r.workerId] = r.Email ? 'pending' : 'no-email'; });
      setEmailStatuses(init);
    }).finally(() => setEmailLoading(false));
  };

  // ── send all emails ──────────────────────────────────────────────────────────
  const sendAllEmails = async () => {
    setEmailSending(true);
    const listName = emailDialog?.name || `${MONTHS[emailDialog?.month]} ${emailDialog?.year}`;

    for (const worker of emailWorkers) {
      const wId = worker.WorkerId || worker.workerId;
      if (!worker.Email) { setEmailStatuses(p => ({ ...p, [wId]: 'no-email' })); continue; }

      setEmailStatuses(p => ({ ...p, [wId]: 'sending' }));
      try {
        const pdf = buildPayslipPdfAttachment(worker, listName, company);
        await notificationsService.sendEmail({
          to:      [worker.Email],
          subject: `Slip Pagash — ${listName} | ${company.CompanyName || 'Kompania'}`,
          html:    buildEmailNotificationHTML(worker, listName, company),
          text:    buildEmailText(worker, listName, company),
          ...(pdf ? { attachments: [pdf] } : {}),
        });
        setEmailStatuses(p => ({ ...p, [wId]: 'sent' }));
      } catch {
        setEmailStatuses(p => ({ ...p, [wId]: 'failed' }));
      }
      // small delay to avoid rate limiting
      await new Promise(res => setTimeout(res, 300));
    }

    setEmailSending(false);
    setEmailDone(true);
  };

  // ── send single email ────────────────────────────────────────────────────────
  const sendSingle = async (worker) => {
    const wId = worker.WorkerId || worker.workerId;
    if (!worker.Email) return;
    const listName = emailDialog?.name || `${MONTHS[emailDialog?.month]} ${emailDialog?.year}`;
    setEmailStatuses(p => ({ ...p, [wId]: 'sending' }));
    try {
      const pdf = buildPayslipPdfAttachment(worker, listName, company);
      await notificationsService.sendEmail({
        to:      [worker.Email],
        subject: `Slip Pagash — ${listName} | ${company.CompanyName || 'Kompania'}`,
        html:    buildEmailNotificationHTML(worker, listName, company),
        text:    buildEmailText(worker, listName, company),
        ...(pdf ? { attachments: [pdf] } : {}),
      });
      setEmailStatuses(p => ({ ...p, [wId]: 'sent' }));
    } catch {
      setEmailStatuses(p => ({ ...p, [wId]: 'failed' }));
    }
  };

  // ── export report CSV ────────────────────────────────────────────────────────
  const exportReport = () => {
    const listName = emailDialog?.name || `${MONTHS[emailDialog?.month]} ${emailDialog?.year}`;
    const rows = [[t('worker.table.employee'), 'Email', t('common.status'), t('salaries.netSalary')]];
    emailWorkers.forEach(w => {
      const wId  = w.WorkerId || w.workerId;
      const net  = payslipNet(w).toFixed(2);
      const st   = emailStatuses[wId] || 'pending';
      const stLb = {
        sent: t('salariesList.sent'),
        failed: t('salariesList.failedChip'),
        'no-email': t('salariesList.noEmailChip'),
        pending: t('salariesList.pending'),
        sending: t('salariesList.sending'),
      }[st] || st;
      rows.push([w.FullName || `${w.FirstName} ${w.LastName}`, w.Email || '—', stLb, net + '€']);
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `raporti-email-${listName}.csv`;
    a.click();
  };

  // ── stats ────────────────────────────────────────────────────────────────────
  const statusCounts = () => {
    const vals = Object.values(emailStatuses);
    return {
      total:   emailWorkers.length,
      sent:    vals.filter(s => s === 'sent').length,
      failed:  vals.filter(s => s === 'failed').length,
      noEmail: vals.filter(s => s === 'no-email').length,
      pending: vals.filter(s => s === 'pending').length,
    };
  };
  const sc = statusCounts();
  const progress = sc.total > 0 ? Math.round(((sc.sent + sc.failed + sc.noEmail) / sc.total) * 100) : 0;

  const emailConfigured = Boolean(config.notificationsApiUrl);

  // ── derived data ──────────────────────────────────────────────────────────
  const uniqueYears = [...new Set(lists.map(l => l.year))].sort((a, b) => b - a);
  const filteredLists = lists.filter((l) => {
    if (filterYear   && String(l.year)  !== String(filterYear))  return false;
    if (filterMonth  && String(l.month) !== String(filterMonth)) return false;
    if (filterStatus && l.status        !== filterStatus)        return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      const name = (l.name || `${MONTHS[l.month]} ${l.year}`).toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  });

  // stats always reflect the filtered view
  const totalLists  = filteredLists.length;
  const openLists   = filteredLists.filter((l) => l.status === 'open').length;
  const closedLists = filteredLists.filter((l) => l.status === 'closed').length;
  const filteredIds = new Set(filteredLists.map(l => l.id));
  const grandSalary = Object.entries(totals)
    .filter(([id]) => filteredIds.has(Number(id)) || filteredIds.has(String(id)))
    .reduce((s, [, t]) => s + (t.salary || 0), 0);

  return (
    <>
      {/* ── Delete dialog ────────────────────────────────────────────────── */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('salariesList.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('salariesList.deleteConfirmText', { name: deleteTarget?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? t('salariesList.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Email dialog ════════════════════════════════════════════════════ */}
      <Dialog
        open={Boolean(emailDialog)}
        onClose={() => !emailSending && setEmailDialog(null)}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <EmailIcon color="primary" />
              <Box>
                <Typography fontWeight={700}>{t('salariesList.sendPayslipsEmail')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {emailDialog?.name || `${MONTHS[emailDialog?.month]} ${emailDialog?.year}`} · {sc.total} {t('salariesList.workers')}
                </Typography>
              </Box>
            </Stack>
            {emailDone && (
              <Button size="small" startIcon={<DownloadIcon />} onClick={exportReport} variant="outlined" sx={{ textTransform: 'none' }}>
                {t('salariesList.exportReport')}
              </Button>
            )}
          </Stack>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ p: 0 }}>

          {/* Progress bar when sending */}
          {emailSending && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600}>{t('salariesList.sending')} {sc.sent + sc.failed + sc.noEmail}/{sc.total}</Typography>
                <Typography variant="caption" color="text.secondary">{progress}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1, height: 6 }} />
            </Box>
          )}

          {/* Summary chips when done */}
          {emailDone && (
            <Box sx={{ px: 3, pt: 2, pb: 1 }}>
              <Alert severity={sc.failed > 0 ? 'warning' : 'success'} sx={{ mb: 1 }}>
                <strong>{t('salariesList.report')}:</strong>{' '}
                {t('salariesList.sentSuccess', { count: sc.sent })}
                {sc.failed > 0 && `, ${sc.failed} ${t('salariesList.failed')}`}
                {sc.noEmail > 0 && `, ${sc.noEmail} ${t('salariesList.noEmail')}`}
              </Alert>
              <Stack direction="row" spacing={1}>
                <Chip size="small" icon={<CheckCircleIcon />} label={`${sc.sent} ${t('salariesList.sent')}`}   color="success" />
                {sc.failed  > 0 && <Chip size="small" icon={<CancelIcon />}       label={`${sc.failed} ${t('salariesList.failedChip')}`}   color="error"   />}
                {sc.noEmail > 0 && <Chip size="small" icon={<WarningAmberIcon />} label={`${sc.noEmail} ${t('salariesList.noEmailChip')}`} color="warning" />}
              </Stack>
            </Box>
          )}

          {/* Worker list */}
          {emailLoading ? (
            <Box sx={{ p: 3 }}>
              {[1,2,3].map(i => <Skeleton key={i} height={48} sx={{ mb: 0.5 }} />)}
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('worker.table.employee')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('salariesList.emailCol')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('salaries.netSalary')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('common.status')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailWorkers.map(w => {
                    const wId   = w.WorkerId || w.workerId;
                    const st    = emailStatuses[wId] || 'pending';
                    const net   = payslipNet(w);
                    const name  = w.FullName || `${w.FirstName} ${w.LastName}`.trim();
                    return (
                      <TableRow key={wId} hover sx={{ bgcolor: st === 'sent' ? alpha('#3E8FA3', 0.04) : st === 'failed' ? alpha('#DC2626', 0.04) : 'inherit' }}>
                        <TableCell sx={{ py: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.main' }}>{initials(name)}</Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{name}</Typography>
                              <Typography variant="caption" color="text.secondary">{w.Position || ''}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          {w.Email
                            ? <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{w.Email}</Typography>
                            : <Chip size="small" label={t('salariesList.noEmailChip')} color="warning" variant="outlined" sx={{ fontSize: 10 }} />}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight={700} color="success.main">{fmt(net)}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <StatusIcon status={st} />
                            <StatusChip status={st} />
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          {w.Email && (st === 'pending' || st === 'failed') && !emailSending && (
                            <Tooltip title={t('salariesList.sendToOne')}>
                              <IconButton size="small" onClick={() => sendSingle(w)} color="primary">
                                <SendIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {st === 'sent' && <CheckCircleIcon sx={{ fontSize: 16, color: '#3E8FA3' }} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Not configured warning */}
          {!emailConfigured && (
            <Box sx={{ px: 3, pb: 2 }}>
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t('salariesList.emailNotConfigured')}
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setEmailDialog(null)} disabled={emailSending} variant="outlined" sx={{ borderRadius: 1.5, textTransform: 'none' }}>
            {t('common.close')}
          </Button>
          {!emailDone ? (
            <Button
              onClick={sendAllEmails}
              variant="contained"
              startIcon={emailSending ? null : <SendIcon />}
              disabled={emailSending || emailLoading || !emailConfigured}
              sx={{ borderRadius: 1.5, textTransform: 'none', minWidth: 180 }}
            >
              {emailSending
                ? <><LinearProgress sx={{ width: 80, mr: 1, height: 4, borderRadius: 2 }} />{t('salariesList.sendingAll')}</>
                : t('salariesList.sendAll', { count: emailWorkers.filter(w => w.Email).length })}
            </Button>
          ) : (
            <Button onClick={() => { setEmailDone(false); setEmailStatuses({}); emailWorkers.forEach(w => { const id = w.WorkerId||w.workerId; setEmailStatuses(p => ({...p,[id]:w.Email?'pending':'no-email'})); }); }}
              variant="outlined" color="warning" sx={{ borderRadius: 1.5, textTransform: 'none' }}>
              {t('salariesList.resend')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ══ Importi nga Excel ═══════════════════════════════════════════════ */}
      <ImportSalariesDialog
        open={importOpen}
        lists={lists}
        onClose={() => setImportOpen(false)}
        onImported={({ message }) => { setToast({ type: 'success', msg: message }); load(); }}
      />

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <Box>
        <Stack spacing={1.5}>

          {/* ── Header card: stats + toolbar + filters ── */}
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            {/* Top bar */}
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <ReceiptLongIcon sx={{ fontSize: 17, color: 'text.disabled', flexShrink: 0 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mr: 0.5 }}>{t('salariesList.monthlyPayroll')}</Typography>
              {loading ? (
                <Skeleton width={220} height={22} sx={{ borderRadius: 4 }} />
              ) : (
                <>
                  {[
                    { label: `${totalLists} ${t('salariesList.total')}`,     color: '#3E8FA3', bg: '#E6F4F8' },
                    { label: `${openLists} ${t('salariesList.open')}`,        color: '#16A34A', bg: '#ECFDF5' },
                    { label: `${closedLists} ${t('salariesList.closed')}`,    color: '#64748B', bg: '#F8FAFC' },
                    { label: fmt(grandSalary),                 color: '#D97706', bg: '#FFFBEB' },
                  ].map(s => (
                    <Chip key={s.label} size="small" label={s.label}
                      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.7rem', height: 22, border: 'none' }} />
                  ))}
                </>
              )}
              <Box sx={{ flex: 1 }} />
              {/* Importi rri pranë "Listë e re" sepse është e njëjta pyetje —
                  nga vjen lista e kësaj periudhe — vetëm me përgjigje tjetër. */}
              <Button variant="outlined" size="small" startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                onClick={() => setImportOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, fontSize: '0.78rem', py: 0.55, flexShrink: 0 }}>
                Importo
              </Button>
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                component={Link} to="/salarieslist/addlist"
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, fontSize: '0.78rem', py: 0.55, flexShrink: 0 }}>
                {t('salariesList.newList')}
              </Button>
            </Box>
            {/* ── Filter bar ─────────────────────────────────────────── */}
            <Box sx={{
              px: 2.5, py: 1.25,
              borderTop: '1px solid', borderColor: 'divider',
              bgcolor: '#F8FAFC',
              display: 'flex', alignItems: 'center', gap: 1.5,
              // wrap në mobile — nowrap+hidden i priste filtrat jashtë ekranit
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              overflow: { md: 'hidden' },
            }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder={t('salariesList.searchPlaceholder')}
                value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: '1 1 160px', minWidth: 0,
                  '& .MuiInputBase-input': { fontSize: '0.82rem' },
                  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' },
                }}
              />

              {/* Divider */}
              <Box sx={{ width: '1px', height: 28, bgcolor: 'divider', flexShrink: 0 }} />

              {/* Year */}
              <FormControl size="small" sx={{ flex: '0 0 100px' }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>{t('salariesList.year')}</InputLabel>
                <Select
                  label={t('salariesList.year')}
                  value={filterYear}
                  onChange={e => setFilterYear(e.target.value)}
                  sx={{ fontSize: '0.82rem', borderRadius: 2, bgcolor: '#fff' }}
                >
                  <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
                  {uniqueYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>

              {/* Month */}
              <FormControl size="small" sx={{ flex: '0 0 120px' }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>{t('common.month')}</InputLabel>
                <Select
                  label={t('common.month')}
                  value={filterMonth}
                  onChange={e => setFilterMonth(e.target.value)}
                  sx={{ fontSize: '0.82rem', borderRadius: 2, bgcolor: '#fff' }}
                >
                  <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
                  {MONTHS.slice(1).map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
                </Select>
              </FormControl>

              {/* Status */}
              <FormControl size="small" sx={{ flex: '0 0 130px' }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>{t('common.status')}</InputLabel>
                <Select
                  label={t('common.status')}
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  sx={{ fontSize: '0.82rem', borderRadius: 2, bgcolor: '#fff' }}
                >
                  <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
                  <MenuItem value="open">{t('salariesList.statusOpen')}</MenuItem>
                  <MenuItem value="closed">{t('salariesList.statusClosed')}</MenuItem>
                </Select>
              </FormControl>

              {/* Clear */}
              {(filterYear || filterMonth || filterStatus || filterSearch) && (
                <Chip
                  size="small"
                  label={t('common.clear')}
                  onDelete={() => { setFilterYear(''); setFilterMonth(''); setFilterStatus(''); setFilterSearch(''); }}
                  sx={{ fontSize: '0.75rem', height: 26, borderRadius: 1.5, fontWeight: 500, flexShrink: 0 }}
                />
              )}

              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap', fontWeight: 500, flexShrink: 0 }}>
                {filteredLists.length} / {lists.length} {t('salariesList.listsCount')}
              </Typography>
            </Box>
          </Paper>

          {/* Toast */}
          {toast && (
            <Alert severity={toast.type} onClose={() => setToast(null)} sx={{ borderRadius: 2 }}>{toast.msg}</Alert>
          )}

          {/* ── Table ── */}
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1, color: '#374151' }}>{t('salariesList.period')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1, color: '#374151', display: { xs: 'none', sm: 'table-cell' } }}>{t('salariesList.employees')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1, color: '#374151' }}>{t('salariesList.grossSalary')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1, color: '#374151', display: { xs: 'none', lg: 'table-cell' } }}>Kontributet</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1, color: '#374151', display: { xs: 'none', lg: 'table-cell' } }}>Tatimi (TAP)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1, color: '#374151' }}>{t('salaries.netSalary')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1, color: '#374151', width: 120 }}>
                      Veprimet
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from({ length: 6 }).map((_, i) => (
                        <TableRow key={i}>
                          {[180, 60, 100, 80, 80, 100, 100].map((w, j) => (
                            <TableCell key={j} sx={{ py: 1.2 }}><Skeleton width={w} height={14} /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    : filteredLists.map((row) => {
                        const mc    = MONTH_COLORS[row.month] || '#3E8FA3';
                        const tot   = totals[row.id] || {};
                        const isOpen = row.status === 'open';
                        return (
                          <TableRow hover key={row.id} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: '#FAFCFE' } }}>

                            <TableCell sx={{ py: 1 }}>
                              <Stack direction="row" spacing={1.25} alignItems="center">
                                <Box sx={{ width: 32, height: 32, borderRadius: 1.5, flexShrink: 0, bgcolor: alpha(mc, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <CalendarMonthIcon sx={{ fontSize: 16, color: mc }} />
                                </Box>
                                <Box>
                                  <Typography variant="body2" fontWeight={700} sx={{ color: '#101828', fontSize: '0.82rem', lineHeight: 1.2 }}>
                                    {row.name || `${MONTHS[row.month]} ${row.year}`}
                                  </Typography>
                                  {/* Nënrreshti përsëriste titullin ("Maj 2026" mbi
                                      "Maj · 2026"). Tani mban gjendjen, ndaj heqja e
                                      kolonës nuk e fshin sinjalin nga rreshti. */}
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    {isOpen
                                      ? <LockOpenIcon sx={{ fontSize: 12, color: '#16A34A' }} />
                                      : <LockIcon sx={{ fontSize: 12, color: '#94A3B8' }} />}
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: isOpen ? '#16A34A' : '#94A3B8', fontWeight: 600 }}>
                                      {isOpen ? 'E hapur' : 'E procesuar'}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, py: 1 }}>
                              <Chip size="small" label={`${tot.count ?? '—'} ${t('salariesList.employeesCount')}`}
                                sx={{ bgcolor: '#F1F5F9', color: '#475569', fontWeight: 600, fontSize: '0.7rem', height: 20, borderRadius: 1 }} />
                            </TableCell>

                            <TableCell align="right" sx={{ py: 1 }}>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#101828', fontVariantNumeric: 'tabular-nums' }}>
                                {tot.salary !== undefined ? fmt(tot.salary) : <Skeleton width={80} height={13} />}
                              </Typography>
                            </TableCell>

                            <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' }, py: 1 }}>
                              <Typography sx={{ fontSize: '0.8rem', color: '#DC2626', fontVariantNumeric: 'tabular-nums' }}>
                                {tot.contributions !== undefined ? `-${fmt(tot.contributions)}` : <Skeleton width={70} height={13} />}
                              </Typography>
                            </TableCell>

                            <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' }, py: 1 }}>
                              <Typography sx={{ fontSize: '0.8rem', color: '#DC2626', fontVariantNumeric: 'tabular-nums' }}>
                                {tot.taxes !== undefined ? `-${fmt(tot.taxes)}` : <Skeleton width={70} height={13} />}
                              </Typography>
                            </TableCell>

                            <TableCell align="right" sx={{ py: 1 }}>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#16A34A', fontVariantNumeric: 'tabular-nums' }}>
                                {tot.net !== undefined ? fmt(tot.net) : <Skeleton width={80} height={13} />}
                              </Typography>
                            </TableCell>


                            <TableCell align="right" sx={{ py: 1 }}>
                              <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                                {/* Tre butona të lidhur si një copë. Jashtë rrinë
                                    veprimet e përditshme — redakto dhe dërgo
                                    fletëpagesat; te menyja rrinë ato që bëhen një
                                    herë në muaj, plus fshirja. */}
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    border: '1px solid', borderColor: 'divider',
                                    borderRadius: 1.5, overflow: 'hidden',
                                    bgcolor: '#fff',
                                    '& .MuiIconButton-root': {
                                      width: 32, height: 30, borderRadius: 0,
                                      color: '#475569',
                                      '&:hover': { bgcolor: '#F1F5F9', color: '#0F172A' },
                                    },
                                    '& .MuiIconButton-root:not(:last-of-type)': {
                                      borderRight: '1px solid', borderColor: 'divider',
                                    },
                                  }}
                                >
                                  <Tooltip title={t('salariesList.tooltipEdit')}>
                                    <IconButton size="small" component={Link} to={`/salarieslist/editlist/${row.id}`}>
                                      <EditIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('salariesList.tooltipEmail')}>
                                    <IconButton size="small" onClick={() => openEmailDialog(row)}>
                                      <EmailIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Printo · Excel · PDF · Fshij">
                                    <IconButton size="small" onClick={(e) => setMenuFor({ row, el: e.currentTarget })}>
                                      {regBusy && regBusy.startsWith(`${row.id}:`)
                                        ? <CircularProgress size={13} />
                                        : <MoreVertIcon sx={{ fontSize: 17 }} />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Një meny e vetme për të gjitha rreshtat — jo një për secilin. */}
            <Menu
              open={Boolean(menuFor)}
              anchorEl={menuFor?.el}
              onClose={() => setMenuFor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ dense: true, sx: { py: 0.5 } }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: 2,
                    minWidth: 196,
                    mt: 0.75,                 // pa këtë, menyja ngjitet te butoni
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 8px 24px rgba(15,23,42,.10)',
                    '& .MuiMenuItem-root': {
                      py: 0.7, px: 1.25, mx: 0.5, borderRadius: 1.25,
                      fontSize: '0.8rem', color: '#334155',
                      '&:hover': { bgcolor: '#F1F5F9' },
                      /* Unaza e fokusit MBETET — është aksesueshmëri, jo zbukurim
                         (index.css: outline 2px #3E8FA3). Por jashtë menysë ajo
                         rri 2px LARG elementit, dhe këtu dilte jashtë zërit si
                         kornizë e shkëputur. Brenda saj rri e ngjitur dhe me të
                         njëjtin rrumbullakim, ndaj lexohet si zgjedhje. */
                      '&:focus-visible': { outlineOffset: '-2px', borderRadius: 1.25 },
                    },
                  },
                },
              }}
            >
              {[
                { key: 'print', label: 'Printo listën', icon: <PrintIcon sx={{ fontSize: 16 }} /> },
                { key: 'excel', label: 'Shkarko Excel',  icon: <GridOnIcon sx={{ fontSize: 16 }} /> },
                { key: 'pdf',   label: 'Shkarko PDF',    icon: <PictureAsPdfIcon sx={{ fontSize: 16 }} /> },
              ].map((a) => (
                <MenuItem key={a.key}
                  onClick={() => { const r = menuFor.row; setMenuFor(null); runRegister(r, a.key); }}>
                  <ListItemIcon sx={{ minWidth: 28, color: '#94A3B8' }}>{a.icon}</ListItemIcon>
                  {a.label}
                </MenuItem>
              ))}
              <Divider sx={{ my: 0.5 }} />
              {/* Fshirja e vetme me ngjyrë — sepse është e vetmja që nuk kthehet. */}
              <MenuItem onClick={() => { const r = menuFor.row; setMenuFor(null); setDeleteTarget(r); }}
                sx={{ color: '#B91C1C !important', '&:hover': { bgcolor: '#FEF2F2 !important' } }}>
                <ListItemIcon sx={{ minWidth: 28, color: '#B91C1C' }}><DeleteIcon sx={{ fontSize: 16 }} /></ListItemIcon>
                Fshij listën
              </MenuItem>
            </Menu>

            {!loading && filteredLists.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <ReceiptLongIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary" variant="body2">
                {lists.length === 0 ? t('salariesList.noLists') : t('salariesList.noMatch')}
              </Typography>
              {lists.length === 0 && (
                <Button variant="contained" startIcon={<AddIcon />} component={Link} to="/salarieslist/addlist" sx={{ mt: 2, textTransform: 'none', borderRadius: 1.5 }}>
                  {t('salariesList.addFirst')}
                  </Button>
                )}
              </Box>
            )}
          </Card>

        </Stack>
      </Box>
    </>
  );
}
